import { useTranslation } from 'react-i18next';
import { useEffect, useState, useMemo } from 'react';
import { useGetMsoolUnits } from '../../../../channels/services/mapping.service';

export interface IEditStep3FormValues {
  selectedPropertyIds: number[];
}

interface IEditStepThreeProps {
  initialSelectedPropertyIds?: number[];
  isActive: boolean;
  onSubmit: (data: IEditStep3FormValues) => void;
  submitRef: React.MutableRefObject<(() => void) | null>;
}

const EditStepThree = ({ initialSelectedPropertyIds, isActive, onSubmit, submitRef }: IEditStepThreeProps) => {
  const { t } = useTranslation();

  const { data: msoolUnitsData, isLoading } = useGetMsoolUnits();
  const units = msoolUnitsData?.units ?? [];

  const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedPropertyIds ?? []);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  // Filter units by search query
  const filteredUnits = useMemo(() => {
    if (!searchQuery.trim()) return units;
    const q = searchQuery.trim().toLowerCase();
    return units.filter(
      (unit) =>
        unit.propertyName?.toLowerCase().includes(q) ||
        unit.channels?.some((ch) => ch.listingName?.toLowerCase().includes(q))
    );
  }, [units, searchQuery]);

  // Expose submit to parent via ref — only when this step is active
  useEffect(() => {
    if (!isActive) return;
    submitRef.current = () => {
      if (selectedIds.length === 0) {
        setError(t('Automation.propertiesRequired'));
        return;
      }
      setError('');
      onSubmit({ selectedPropertyIds: selectedIds });
    };
  }, [isActive, selectedIds, onSubmit, submitRef, t]);

  const toggleItem = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    if (error) setError('');
  };

  const isAllSelected = filteredUnits.length > 0 && filteredUnits.every((u) => selectedIds.includes(u.propertyId));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const filteredIds = new Set(filteredUnits.map((u) => u.propertyId));
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      const filteredIds = filteredUnits.map((u) => u.propertyId);
      setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
    if (error) setError('');
  };

  const getAddress = (unit: (typeof units)[0]) => {
    if (unit.channels && unit.channels.length > 0 && unit.channels[0].listingName) {
      return unit.channels[0].listingName;
    }
    return '—';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#8E98A1' }}>
          {t('Automation.noProperties')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      {/* Search Input */}
      <div
        style={{
          backgroundColor: '#FAFAFA',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E9E9E7',
            borderRadius: 8,
            padding: '8px 12px',
            gap: 8,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M7.33 12.66A5.33 5.33 0 1 0 7.33 2a5.33 5.33 0 0 0 0 10.66ZM14 14l-2.9-2.9"
              stroke="#8E98A1"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            placeholder={t('Automation.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'Tajawal, sans-serif',
              fontSize: 14,
              color: '#331860',
              direction: 'inherit',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: '#FAFAFA',
          borderRadius: 12,
          padding: 16,
        }}
      >
        {/* Table Header */}
        <div
          className="flex items-center w-full"
          style={{
            gap: 12,
            padding: '8px 0',
            borderBottom: '1px solid #E9E9E7',
          }}
        >
          {/* Select All checkbox */}
          <button
            type="button"
            onClick={toggleSelectAll}
            className="flex items-center cursor-pointer outline-none"
            style={{
              gap: 8,
              background: 'none',
              border: 'none',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <div style={{ padding: '0 8px' }}>
              {isAllSelected ? (
                <div
                  className="flex items-center justify-center overflow-hidden"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 6,
                    backgroundColor: '#5B3FFF',
                    border: '0.538px solid #5B3FFF',
                  }}
                >
                  <svg width="8" height="5" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              ) : (
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 6,
                    backgroundColor: '#FFFFFF',
                    border: '0.538px solid #CACACA',
                  }}
                />
              )}
            </div>
            <p
              style={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                color: '#8E98A1',
                lineHeight: '12px',
                margin: 0,
              }}
            >
              {t('Automation.selectAll')}
            </p>
          </button>
          <div className="flex-1 min-w-0">
            <p
              style={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                color: '#8E98A1',
                lineHeight: '12px',
                margin: 0,
                textAlign: 'start',
              }}
            >
              {t('Automation.property')}
            </p>
          </div>
          <div className="flex-1 min-w-0">
            <p
              style={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                color: '#8E98A1',
                lineHeight: '12px',
                margin: 0,
                textAlign: 'start',
              }}
            >
              {t('Automation.address')}
            </p>
          </div>
        </div>

        {/* Table Rows */}
        <div className="flex flex-col">
          {filteredUnits.map((unit, index) => {
            const isChecked = selectedIds.includes(unit.propertyId);
            const isLast = index === filteredUnits.length - 1;

            return (
              <button
                key={unit.propertyId}
                type="button"
                onClick={() => toggleItem(unit.propertyId)}
                className="flex items-center w-full cursor-pointer outline-none"
                style={{
                  gap: 12,
                  padding: '8px 0',
                  background: isChecked ? 'rgba(239, 236, 255, 0.3)' : 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: isLast && !isChecked ? 'none' : `1px solid ${isChecked ? '#EFECFF' : '#E9E9E7'}`,
                }}
              >
                {/* Checkbox */}
                <div
                  className="shrink-0 flex items-center justify-center"
                  style={{ width: 30, paddingBottom: 4 }}
                >
                  <div style={{ padding: '8px 8px 4px 8px' }}>
                    {isChecked ? (
                      <div
                        className="flex items-center justify-center overflow-hidden"
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 6,
                          backgroundColor: '#5B3FFF',
                          border: '0.538px solid #5B3FFF',
                        }}
                      >
                        <svg
                          width="8"
                          height="5"
                          viewBox="0 0 8 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 3L3 5L7 1"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 6,
                          backgroundColor: '#FFFFFF',
                          border: '0.538px solid #CACACA',
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Property: image + name */}
                <div className="flex-1 min-w-0 flex items-center" style={{ gap: 8 }}>
                  {unit.imageUrl ? (
                    <div
                      style={{
                        width: 51,
                        height: 30,
                        borderRadius: 2,
                        overflow: 'hidden',
                        flexShrink: 0,
                        position: 'relative',
                      }}
                    >
                      <img
                        src={unit.imageUrl}
                        alt={unit.propertyName || ''}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: 2,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: 51,
                        height: 30,
                        borderRadius: 2,
                        backgroundColor: '#E9E9E7',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <p
                    className="truncate text-start"
                    style={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontWeight: 700,
                      fontSize: 12,
                      color: '#331860',
                      lineHeight: '12px',
                      margin: 0,
                    }}
                  >
                    {unit.propertyName || '—'}
                  </p>
                </div>

                {/* Address */}
                <div className="flex-1 min-w-0">
                  <p
                    className="truncate text-start"
                    style={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontWeight: 400,
                      fontSize: 12,
                      color: '#8E98A1',
                      lineHeight: '12px',
                      margin: 0,
                    }}
                  >
                    {getAddress(unit)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {filteredUnits.length === 0 && (
          <div className="flex items-center justify-center" style={{ padding: '24px 0' }}>
            <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#8E98A1', margin: 0 }}>
              {t('Automation.noProperties')}
            </p>
          </div>
        )}
      </div>

      {error && (
        <p
          style={{
            fontFamily: 'Tajawal, sans-serif',
            fontSize: 12,
            color: '#EF4444',
            margin: 0,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default EditStepThree;
